import { L } from "../utils/i18n";
import React, { useLayoutEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import useSWR from "swr";
import { useHistory } from "react-router";
import ServiceWrapper from "../components/ServiceWrapper";
import CoverTile from "../components/CoverTile";
import HomeExtras from "../components/HomeExtras";
import BizInfo from "../components/BizInfo";
import auth from "../utils/auth";
import { usePreview } from "../utils/preview";
import { useScoreCount } from "../utils/scoreCount";
import { fmtViews, hcEv, IHotClip, thumb, thumbFix } from "./HotClips";
import { noPurchaseUI } from "../utils/billing";

/**
 * 새 디자인 홈 (2026-10, 미리 써보기 계정에서만 — utils/beta.ts).
 * 검색창 · 새로 올라온 악보(가로 카드) · 인기 TOP 100(일간/주간/월간) · 악보 신청.
 * 앨범 사진이 아직 없어서 곡마다 색 타일 + 제목 첫 글자로 대신한다.
 */
interface ISongItem {
  id: number;
  title: string;
  singer: string;
  image_url?: string | null;
}

const TILES = [
  ["#20263a", "#16181e"],
  ["#2a2416", "#16181e"],
  ["#1c2a24", "#16181e"],
  ["#2a1c26", "#16181e"],
  ["#1c2430", "#16181e"],
];

const tileOf = (id: number) => TILES[id % TILES.length];
const initial = (title: string) => (title || "").trim().charAt(0);

const Tile: React.FC<{ song: ISongItem; size: number; radius: number }> = ({ song, size, radius }) => {
  const [a, b] = tileOf(song.id);
  return song.image_url ? (
    <TileImg src={song.image_url} alt="" style={{ width: size, height: size, borderRadius: radius }} />
  ) : (
    <TileBox style={{ width: size, height: size, borderRadius: radius, background: `linear-gradient(160deg, ${a}, ${b})`, fontSize: size * 0.42 }}>
      {initial(song.title)}
    </TileBox>
  );
};

const TABS: [string, string][] = [
  ["daily", "일간"],
  ["week", "주간"],
  ["month", "월간"],
];

const HomeBeta: React.FC = () => {
  const history = useHistory();
  const scoreCount = useScoreCount();
  const [filter, setFilter] = useState("daily");
  const [q, setQ] = useState("");
  const { data: current } = useSWR(`/main/index?popularity_type=${filter}`);
  useSWR(`/main/index?popularity_type=daily`);
  useSWR(`/main/index?popularity_type=week`);
  useSWR(`/main/index?popularity_type=month`);
  const last = useRef<any>(null);
  if (current) last.current = current;
  const data = current || last.current; // App 의 fetcher 가 응답의 data 를 꺼내 줌
  const news: ISongItem[] = data?.new || [];
  const top: ISongItem[] = data?.popularities || [];

  const open = (id: number) => history.push(`/notes/${id}`);
  // PC: 새 악보는 오른쪽 인기 TOP 100 상자 아래 끝선까지 들어가는 줄만 (사장님 2026-10-08). 휴대폰은 옆으로 넘기며 90곡
  const topRef = useRef<HTMLElement>(null);
  const newRef = useRef<HTMLDivElement>(null);
  const [newCount, setNewCount] = useState(90);
  const [newGap, setNewGap] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    const fit = () => {
      const grid = newRef.current, box = topRef.current;
      if (!grid || !box || window.innerWidth < 1024) { setNewCount(90); setNewGap(undefined); return; }
      const cards = Array.from(grid.children) as HTMLElement[];
      if (!cards.length) return;
      const firstTop = cards[0].offsetTop;
      const cols = Math.max(1, cards.filter((c) => c.offsetTop === firstTop).length);
      const cardH = cards[0].offsetHeight;
      const gap = 22;
      const room = box.getBoundingClientRect().bottom - grid.getBoundingClientRect().top;
      // 줄 수는 가까운 쪽으로 반올림하고, 줄 간격(12~32px)을 조금 늘이거나 줄여 마지막 줄이 상자 끝선에 딱 맞게
      const rows = Math.max(1, Math.round((room + gap) / (cardH + gap)));
      const g = rows > 1 ? (room - rows * cardH) / (rows - 1) : gap;
      setNewGap(Math.max(12, Math.min(32, g)));
      setNewCount(Math.min(90, rows * cols));
    };
    fit();
    const t = setTimeout(fit, 400); // 그림이 늦게 그려진 뒤 한 번 더
    window.addEventListener("resize", fit);
    return () => { clearTimeout(t); window.removeEventListener("resize", fit); };
  }, [news.length, top.length, filter]);
  const { data: hot } = useSWR<IHotClip[]>(SHOW_HOT_ON_HOME ? "/hotclips" : null);
  // 맛보기가 켜져 있고 구독 중이 아니면 '이번 주 무료 곡'
  const preview = usePreview();
  const freeSongs = preview && preview.enabled && !["0", "1", "4"].includes(auth.getPayStatus() || "") ? preview.songs : [];
  const clips: IHotClip[] = Array.isArray(hot) ? hot : [];

  // 목록을 옆으로 밀면 일간 → 주간 → 월간 (반대로 밀면 거꾸로)
  const touch = useRef<{ x: number; y: number } | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const t = touch.current;
    touch.current = null;
    if (!t) return;
    const dx = e.changedTouches[0].clientX - t.x;
    const dy = e.changedTouches[0].clientY - t.y;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    const i = TABS.findIndex(([v]) => v === filter);
    const next = dx < 0 ? Math.min(TABS.length - 1, i + 1) : Math.max(0, i - 1);
    if (next !== i) {
      setSlide(dx < 0 ? "left" : "right");
      setFilter(TABS[next][0]);
    }
  };
  const [slide, setSlide] = useState<"left" | "right" | "">("");
  const search = () => {
    const v = q.trim();
    if (v) history.push(`/search/${encodeURIComponent(v)}`);
  };

  const myScoreOn = true; // 내 악보 만들기: 2026-10-09 사장님 지시로 전체 공개 (먼저 써 보는 계정만 켜려면 useIsTester())

  return (
    <ServiceWrapper isFooterRendered={false}>
    <Page>
      <SearchBar className="a-search"
        onSubmit={(e) => {
          e.preventDefault();
          search();
        }}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="곡명, 가수, 초성으로 검색" type="search" />
      </SearchBar>

      <div className="a-tools" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Tools>
        <ToolBtn type="button" onClick={() => history.push("/tuner")}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 2v7a3 3 0 0 0 6 0V2M12 12v10M8 22h8" /></svg>
          튜너
        </ToolBtn>
        {/* 2026-10-09 사장님: 전체악보는 아래 탭과 겹쳐서 빼고 그 자리에 내 악보 만들기 (모두에게 켤 때까지는 사장님 계정만) */}
        {myScoreOn ? (
          <ToolBtn type="button" onClick={() => history.push("/my-scores")}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></svg>
            악보 만들기
          </ToolBtn>
        ) : (
          <ToolBtn type="button" onClick={() => history.push("/full-sheet-music")}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5h16M4 10h16M4 15h10M4 20h7" /></svg>
            전체악보
          </ToolBtn>
        )}
        <ToolBtn type="button" onClick={() => history.push("/hotclip")}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="6" y="3" width="12" height="18" rx="3" /><path d="m10.5 9.5 4 2.5-4 2.5z" /></svg>
          핫클립
        </ToolBtn>
        <ToolBtn type="button" onClick={() => history.push("/request")}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
          악보 신청
        </ToolBtn>
      </Tools>
      </div>

      {freeSongs.length > 0 && (
        <Section className="a-free">
          <SectionHead>
            <h2>이번 주 무료 곡</h2>
            <FreeNote>끝까지 무료로 쳐 보세요</FreeNote>
          </SectionHead>
          <Cards>
            {freeSongs.map((s) => (
              <Card key={s.id} type="button" onClick={() => open(s.id)}>
                <FreeTile>
                  <CoverTile id={s.id} title={s.title} size={112} radius={14} />
                  <em>무료</em>
                </FreeTile>
                <CardTitle>{s.title}</CardTitle>
                <CardSub>{s.singer}</CardSub>
              </Card>
            ))}
          </Cards>
        </Section>
      )}

      <Section className="a-new">
        <SectionHead>
          <h2>새로 올라온 악보{news.length > 0 && <small>{L(`최근 ${Math.min(newCount, news.length)}곡`, `latest ${Math.min(newCount, news.length)}`)}</small>}</h2>
          <MoreLink type="button" onClick={() => history.push("/full-sheet-music")}>전체보기</MoreLink>
        </SectionHead>
        <Cards ref={newRef} style={newGap !== undefined ? { rowGap: newGap } : undefined}>
          {news.slice(0, newCount).map((s) => (
            <Card key={s.id} type="button" onClick={() => open(s.id)}>
              <CoverTile id={s.id} title={s.title} size={112} radius={14} />
              <CardTitle>{s.title}</CardTitle>
              <CardSub>{s.singer}</CardSub>
            </Card>
          ))}
          {!news.length && [0, 1, 2].map((i) => <CardSkeleton key={i} />)}
        </Cards>
      </Section>

      {SHOW_HOT_ON_HOME && clips.length > 0 && (
        <Section className="a-hot">
          <SectionHead>
            <h2>핫클립</h2>
            <MoreLink type="button" onClick={() => history.push("/hotclip")}>전체보기</MoreLink>
          </SectionHead>
          <Cards>
            {clips.slice(0, 12).map((c) => (
              <Clip
                key={c.id}
                type="button"
                onClick={() => {
                  hcEv("clk", c.id);
                  history.push(`/hotclip?v=${c.id}`);
                }}
              >
                <ClipImg
                  src={thumb(c.id)}
                  alt=""
                  loading="lazy"
                  onError={thumbFix(c.id).onError}
                  onLoad={(e) => {
                    thumbFix(c.id).onLoad(e);
                    hcEv("imp", c.id); // 늦게 불러오는 그림이라 화면 근처에 왔을 때 = 보였다고 봄
                  }}
                />
                <ClipViews>
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
                  {fmtViews(c.views)}
                </ClipViews>
                <ClipTitle>{c.music ? c.music.title : c.title}</ClipTitle>
              </Clip>
            ))}
          </Cards>
        </Section>
      )}

      <Section className="a-top" ref={topRef}>
        <SectionHead>
          <h2>인기 TOP 100</h2>
          <Segmented role="group" aria-label="인기 기간">
            {TABS.map(([v, label]) => (
              <SegBtn key={v} type="button" active={filter === v} onClick={() => setFilter(v)}>
                {label}
              </SegBtn>
            ))}
          </Segmented>
        </SectionHead>
        <List key={filter} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} className={slide ? `slide-${slide}` : ""}>
          {top.slice(0, 100).map((s, i) => (
            <Row key={`${filter}-${s.id}`} type="button" onClick={() => open(s.id)}>
              <Rank top={i < 3}>{i + 1}</Rank>
              <CoverTile id={s.id} title={s.title} size={44} radius={8} />
              <RowText>
                <RowTitle>{s.title}</RowTitle>
                <RowSub>{s.singer}</RowSub>
              </RowText>
            </Row>
          ))}
          {!top.length && [0, 1, 2, 3, 4].map((i) => <RowSkeleton key={i} />)}
        </List>
      </Section>

      <Request className="a-req" type="button" onClick={() => history.push("/request")}>
        <div>
          <strong>찾는 곡이 없나요?</strong>
          <span>신청하면 악보를 만들어 올려드려요</span>
        </div>
        <em>신청하기</em>
      </Request>
      {!["0", "1", "4"].includes(auth.getPayStatus() || "") && !noPurchaseUI() && (
        <Plan className="a-plan" type="button" onClick={() => history.push("/pricing")}>
          <div>
            <strong>월 3,300원으로 모든 악보</strong>
            <span>{scoreCount}곡이 넘는 코드 악보 · 언제든 해지</span>
          </div>
          <em>구독 안내</em>
        </Plan>
      )}

      <div className="a-biz">
        <BizInfo />
      </div>
      <HomeExtras />
    </Page>
    </ServiceWrapper>
  );
};

export default HomeBeta;

const Page = styled.div`
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
  box-sizing: border-box;
  padding: 12px 20px 110px;
  display: flex;
  flex-direction: column;
  gap: 26px;
  color: #f3f4f6;
  font-family: "Noto Sans KR", "Apple SD Gothic Neo", sans-serif;

  /* 태블릿: 폭을 넓히고 카드는 줄 바꿔 펼침 */
  @media (min-width: 700px) {
    max-width: 960px;
    padding: 20px 32px 110px;
    & .a-new > div:last-of-type,
    & .a-free > div:last-of-type,
    & .a-hot > div:last-of-type {
      flex-wrap: wrap;
      overflow: visible;
      margin: 0;
      padding: 0;
      row-gap: 16px;
    }
  }

  /* PC 같은 넓은 화면: 왼쪽(검색·도구·새 악보·핫클립) + 오른쪽(인기 TOP 100·악보 신청) 두 단 */
  @media (min-width: 1024px) {
    max-width: 1240px;
    padding: 28px 40px 60px;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 400px;
    grid-template-areas:
      "search top"
      "tools top"
      "free top"
      "new top"
      "hot top"
      "req top"
      "plan top"
      "biz top"
      ". top";
    grid-template-rows: auto auto auto auto auto auto auto auto 1fr;
    column-gap: 48px;
    row-gap: 28px;
    align-items: start;
    & > .a-search { grid-area: search; }
    & > .a-tools { grid-area: tools; margin-top: 0; }
    & > .a-new { grid-area: new; }
    & > .a-free { grid-area: free; }
    & > .a-hot { grid-area: hot; }
    /* 인기 TOP 100: 상자 안에 20곡이 보이고 나머지는 상자 안 스크롤, 줄 간격 넉넉히 (사장님 2026-10-08) */
    & > .a-top {
      grid-area: top;
      display: flex;
      flex-direction: column;
      padding: 18px 8px 8px 18px;
      border-radius: 18px;
      background: linear-gradient(180deg, #15171d 0%, #111318 100%);
      border: 1px solid #23262e;
      box-sizing: border-box;
    }
    & > .a-top > div:first-of-type { padding-right: 10px; }
    & > .a-top > div:last-of-type {
      max-height: calc(72px * 20);
      overflow-y: auto;
      padding-right: 6px;
      scrollbar-width: thin;
      scrollbar-color: #2f3440 transparent;
    }
    & > .a-top > div:last-of-type > button {
      flex: none; /* 상자 높이에 맞춰 줄이 눌려 작아지지 않게 */
      height: 72px;
      padding: 0 10px 0 4px;
      gap: 16px;
      border-radius: 12px;
      transition: background 0.15s;
    }
    & > .a-top > div:last-of-type > button:hover { background: rgba(255, 255, 255, 0.04); }
    & > .a-top > div:last-of-type > button > span:last-child { gap: 4px; }
    & > .a-top > div:last-of-type::-webkit-scrollbar { width: 6px; }
    & > .a-top > div:last-of-type::-webkit-scrollbar-thumb { background: #2f3440; border-radius: 3px; }
    /* 새로 올라온 악보: 칸을 꽉 채운 격자 */
    & .a-new > div:last-of-type {
      display: grid !important;
      grid-template-columns: repeat(auto-fill, minmax(124px, 1fr));
      column-gap: 14px;
      row-gap: 22px;
    }
    & .a-new > div:last-of-type > button { width: auto; min-width: 0; transition: transform 0.15s; }
    & .a-new > div:last-of-type > button:hover { transform: translateY(-3px); }
    & .a-new > div:last-of-type > button > *:first-child {
      width: 100% !important;
      height: auto !important;
      aspect-ratio: 1 / 1;
      box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35);
    }
    & .a-new h2 small {
      margin-left: 8px;
      font-size: 13px;
      font-weight: 500;
      color: #7c8392;
    }
    & > .a-req { grid-area: req; }
    & > .a-biz { grid-area: biz; }
    & > .a-plan { grid-area: plan; }
    /* 마우스로는 옆으로 밀기 어려우니 카드를 줄 바꿔 펼쳐 보여 줌 */
    & .a-new > div:last-of-type,
    & .a-free > div:last-of-type,
    & .a-hot > div:last-of-type {
      flex-wrap: wrap;
      overflow: visible;
      margin: 0;
      padding: 0;
      row-gap: 16px;
    }
  }
`;

const SearchBar = styled.form`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 48px;
  padding: 0 16px;
  border-radius: 14px;
  background: #16181e;
  border: 1px solid #2a2e38;

  & > svg {
    flex: none;
    width: 18px;
    height: 18px;
    fill: none;
    stroke: #a3a9b5;
    stroke-width: 2;
    stroke-linecap: round;
  }
  & > input {
    flex: 1;
    min-width: 0;
    height: 46px;
    border: 0;
    outline: 0;
    background: transparent;
    color: #f3f4f6;
    font-size: 15px;
  }
  & > input::placeholder {
    color: #7c8392;
  }
`;

// 핫클립은 첫 화면에 영상 목록으로 보여 주지 않고 빠른 메뉴 '핫클립' 버튼으로만 (2026-10-05 사장님:
// "첫 화면엔 노출하지 말고 별도 메뉴로, 그 메뉴 인기가 많으면 첫 화면으로"). 다시 보이려면 true.
const SHOW_HOT_ON_HOME = false;

const Tools = styled.div`
  display: flex;
  gap: 8px;
  margin-top: -10px;
  /* 휴대폰: 버튼 4개라 아이콘 위·글자 아래로 */
  @media (max-width: 699px) {
    & > button {
      flex-direction: column;
      height: 62px;
      gap: 4px;
      font-size: 13px;
    }
  }
`;

const ToolBtn = styled.button`
  flex: 1;
  height: 44px;
  border-radius: 12px;
  border: 1px solid #2a2e38;
  background: #16181e;
  color: #f3f4f6;
  font-size: clamp(12px, 3.5vw, 14px);
  white-space: nowrap; /* 악보 만들기가 좁은 폰에서 두 줄로 깨지지 않게 */
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  & > svg {
    width: 16px;
    height: 16px;
    fill: none;
    stroke: #ffb547;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  & > h2 > small {
    margin-left: 8px;
    font-size: 12px;
    font-weight: 500;
    color: #7c8392;
  }

  & > h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #f3f4f6;
  }
`;

const MoreLink = styled.button`
  border: 0;
  background: none;
  padding: 6px 0;
  color: #8fb0ff;
  font-size: 13px;
  cursor: pointer;
`;

const Cards = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  margin: 0 -20px;
  padding: 0 20px 4px;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Clip = styled.button`
  position: relative;
  flex: 0 0 112px;
  height: 200px;
  padding: 0;
  border: 0;
  border-radius: 14px;
  overflow: hidden;
  background: #16181e;
  cursor: pointer;
  text-align: left;
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(#0000 50%, #000d);
  }
`;

const ClipImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ClipViews = styled.span`
  position: absolute;
  z-index: 1;
  top: 8px;
  left: 8px;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px 6px 2px 4px;
  border-radius: 8px;
  background: #0009;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  & > svg {
    width: 12px;
    height: 12px;
    fill: #fff;
  }
`;

const ClipTitle = styled.span`
  position: absolute;
  z-index: 1;
  left: 8px;
  right: 8px;
  bottom: 8px;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: keep-all;
  overflow-wrap: anywhere;
`;

const Card = styled.button`
  flex: 0 0 112px;
  width: 112px;
  min-width: 0; /* 긴 제목이 카드 폭을 늘리지 않게 (말줄임) */
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0;
  border: 0;
  background: none;
  text-align: left;
  cursor: pointer;
  color: inherit;
`;

const CardTitle = styled.span`
  margin-top: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #f3f4f6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardSub = styled.span`
  font-size: 12px;
  color: #a3a9b5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardSkeleton = styled.div`
  flex: 0 0 112px;
  height: 150px;
  border-radius: 14px;
  background: #16181e;
`;

const TileBox = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #2a2e38;
  box-sizing: border-box;
  color: #ffb547;
  font-weight: 700;
`;

const TileImg = styled.img`
  flex: none;
  object-fit: cover;
`;

const Segmented = styled.div`
  display: flex;
  padding: 3px;
  border-radius: 10px;
  background: #16181e;
  border: 1px solid #2a2e38;
`;

const SegBtn = styled.button<{ active: boolean }>`
  height: 30px;
  padding: 0 12px;
  border: 0;
  border-radius: 8px;
  background: ${({ active }) => (active ? "#2b3550" : "transparent")};
  color: ${({ active }) => (active ? "#f3f4f6" : "#a3a9b5")};
  font-size: 13px;
  font-weight: ${({ active }) => (active ? 600 : 400)};
  cursor: pointer;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  touch-action: pan-y;

  &.slide-left {
    animation: csSlideLeft 0.22s ease-out;
  }
  &.slide-right {
    animation: csSlideRight 0.22s ease-out;
  }
  @keyframes csSlideLeft {
    from { transform: translateX(40px); opacity: 0.3; }
    to { transform: none; opacity: 1; }
  }
  @keyframes csSlideRight {
    from { transform: translateX(-40px); opacity: 0.3; }
    to { transform: none; opacity: 1; }
  }
`;

const Row = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  height: 62px;
  padding: 0;
  border: 0;
  border-bottom: 1px solid #1e2129;
  background: none;
  text-align: left;
  cursor: pointer;
  color: inherit;
`;

const RowSkeleton = styled.div`
  height: 62px;
  border-bottom: 1px solid #1e2129;
`;

const Rank = styled.span<{ top: boolean }>`
  width: 22px;
  flex: none;
  font-size: 15px;
  font-weight: 700;
  color: ${({ top }) => (top ? "#ffb547" : "#7c8392")};
`;

const RowText = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const RowTitle = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: #f3f4f6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RowSub = styled.span`
  font-size: 12px;
  color: #a3a9b5;
`;

const FreeNote = styled.span`
  font-size: 13px;
  color: #3ddc97;
`;

// 무료 곡 표지 위 '무료' 표시
const FreeTile = styled.div`
  position: relative;
  & > em {
    position: absolute;
    top: 6px;
    left: 6px;
    padding: 2px 7px;
    border-radius: 8px;
    background: #3ddc97;
    color: #0d0e12;
    font-size: 11px;
    font-weight: 800;
    font-style: normal;
  }
`;

// 구독 안내로 가는 띠 (구독 중이 아닌 사람에게만)
const Plan = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  border-radius: 16px;
  border: 1px solid #3a3220;
  background: #1a1710;
  text-align: left;
  cursor: pointer;
  color: inherit;
  & > div {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  & strong {
    font-size: 15px;
    font-weight: 700;
    color: #ffb547;
  }
  & span {
    font-size: 13px;
    color: #a3a9b5;
  }
  & > em {
    font-style: normal;
    height: 40px;
    padding: 0 16px;
    border-radius: 12px;
    background: #ffb547;
    color: #0d0e12;
    font-size: 14px;
    font-weight: 700;
    display: flex;
    align-items: center;
  }
`;

const Request = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  border-radius: 16px;
  border: 1px dashed #3a3f4b;
  background: none;
  text-align: left;
  cursor: pointer;
  color: inherit;

  & > div {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  & strong {
    font-size: 15px;
    font-weight: 600;
    color: #f3f4f6;
  }
  & span {
    font-size: 13px;
    color: #a3a9b5;
  }
  & > em {
    font-style: normal;
    height: 40px;
    padding: 0 16px;
    border-radius: 12px;
    background: #5b8cff;
    color: #0d0e12;
    font-size: 14px;
    font-weight: 600;
    display: flex;
    align-items: center;
  }
`;

